import { useMemo } from 'react';
import { useAccount, useChainId } from 'wagmi';
import { formatUnits } from 'viem';
import { useUSDCRewardsData } from '@/hooks/useUSDCRewardsData';
import { useFetchDividendPayouts } from '@/features/tokenized-equity/hooks/useFetchDividendPayouts';
import { useHolderEquityTokens } from '@/features/tokenized-equity/onchain/useHolderEquityTokens';
import { assetBySymbol, assetByCbrn } from '@/onchain/assets';
import type { DividendPayout } from '@/features/tokenized-equity/grpc/types';

// TODO(OM-464): Replace with real USD pricing once TradFi data source is integrated.
const MOCK_USD_PRICE = 1;

// Types for Tokenized Equities tab
export type TokenizedEquityAsset = {
  id: string;
  name: string;
  symbol: string;
  imageUrl: string;
  tokenBalance: number;
  shares: number;
  usdcAmount: number;
};

export const EventType = {
  CASH_DIVIDEND: 'cash-dividend',
  REVERSE_STOCK_SPLIT: 'reverse-stock-split',
  STOCK_SPLIT: 'stock-split',
} as const;

export type EventTypeValue = (typeof EventType)[keyof typeof EventType];

export const ImpactUnit = {
  USDC: 'USDC',
  SHARES: 'Shares',
} as const;

export type ImpactUnitValue = (typeof ImpactUnit)[keyof typeof ImpactUnit];

export type EquityHistoryEvent = {
  id: string;
  eventType: EventTypeValue;
  eventLabel: string;
  ratioLabel?: string;
  tokenName: string;
  tokenSymbol: string;
  tokenImageUrl: string;
  impact: number;
  impactUnit: ImpactUnitValue;
  date: string;
};

export type EquitiesOverviewData = {
  largestAsset: {
    symbol: string;
    imageUrl: string;
    percentage: number;
  };
  numberOfTokenAssets: number;
  nextUpcomingEvent: string;
};

export type UpcomingEvent = {
  id: string;
  eventType: EventTypeValue;
  eventLabel: string;
  tokenName: string;
  tokenSymbol: string;
  tokenImageUrl: string;
  detail: string;
  recordDate: string;
  executionDate: string;
};

/**
 * Maps a proto DividendType string enum to the UI event type and label.
 * proto-loader with `enums: String` returns enum names as strings (e.g. "DIVIDEND_TYPE_CASH").
 */
function mapDividendType(dt: string): {
  eventType: EventTypeValue;
  eventLabel: string;
  impactUnit: ImpactUnitValue;
} {
  switch (dt) {
    case 'DIVIDEND_TYPE_STOCK':
      return {
        eventType: EventType.STOCK_SPLIT,
        eventLabel: 'Stock dividend',
        impactUnit: ImpactUnit.SHARES,
      };
    case 'DIVIDEND_TYPE_CASH':
    default:
      return {
        eventType: EventType.CASH_DIVIDEND,
        eventLabel: 'Cash dividend',
        impactUnit: ImpactUnit.USDC,
      };
  }
}

/**
 * Formats a protobuf Timestamp (seconds + nanos) into a locale date string.
 */
function formatPaidAt(paidAt: DividendPayout['paid_at']): string {
  if (!paidAt) return '';
  const ms = Number(paidAt.seconds) * 1000 + Math.floor(paidAt.nanos / 1_000_000);
  return new Date(ms).toLocaleDateString();
}

/**
 * Transforms a DividendPayout from the API into an EquityHistoryEvent for the History tab.
 * Uses the cbrn field to resolve token metadata (name, symbol, image) from the asset registry.
 */
function dividendPayoutToHistoryEvent(payout: DividendPayout, index: number): EquityHistoryEvent {
  const config = assetByCbrn(payout.cbrn);
  const { eventType, eventLabel, impactUnit } = mapDividendType(payout.dividend_type);

  return {
    id: payout.corporate_action_id || String(index),
    eventType,
    eventLabel,
    tokenName: config?.name ?? 'Unknown',
    tokenSymbol: config?.displaySymbol ?? 'Unknown',
    tokenImageUrl: config?.imageUrl ?? '',
    impact: parseFloat(payout.amount),
    impactUnit,
    date: formatPaidAt(payout.paid_at),
  };
}

const mockEquitiesOverview: EquitiesOverviewData = {
  largestAsset: {
    symbol: 'tNVDA',
    imageUrl: '/equities/Nvidia.svg',
    percentage: 12.59,
  },
  numberOfTokenAssets: 3,
  nextUpcomingEvent: '6/20/2025',
};

export function useTangorPortfolioData() {
  const { showRewardsCard } = useUSDCRewardsData();

  // Equities data — real on-chain balances via Tangor subgraph
  const { address } = useAccount();
  const chainId = useChainId();
  const { equityTokens, isLoading: isEquityLoading } = useHolderEquityTokens(address, chainId);

  // Fetch past dividend payouts for the connected wallet
  const { data: dividendPayoutsData } = useFetchDividendPayouts({
    recipient_address: address,
    dividend_types: ['CASH', 'STOCK'],
    enabled: !!address,
  });

  const { equityAssets, totalEquitiesValue } = useMemo(() => {
    const assets: TokenizedEquityAsset[] = equityTokens
      .map((token) => {
        const tokenBalance = parseFloat(formatUnits(token.balance, token.decimals));
        const shares = parseFloat(formatUnits(token.shareBalance, token.decimals));
        const usdcAmount = tokenBalance * MOCK_USD_PRICE;
        const config = assetBySymbol(token.symbol);

        return {
          id: token.symbol,
          name: token.name,
          symbol: token.symbol,
          imageUrl: config?.imageUrl ?? '',
          tokenBalance,
          shares,
          usdcAmount,
        };
      })
      .sort((a, b) => b.usdcAmount - a.usdcAmount);

    const total = assets.reduce((sum, a) => sum + a.usdcAmount, 0);
    return { equityAssets: assets, totalEquitiesValue: total };
  }, [equityTokens]);

  // Transform dividend payouts into equity history events
  const equityHistory = useMemo<EquityHistoryEvent[]>(
    () =>
      (dividendPayoutsData?.dividend_payouts ?? []).map((payout, index) =>
        dividendPayoutToHistoryEvent(payout, index),
      ),
    [dividendPayoutsData],
  );

  const equitiesOverview = useMemo(() => mockEquitiesOverview, []);

  return {
    showRewardsCard,
    // Equities
    equityAssets,
    equityHistory,
    equitiesOverview,
    totalEquitiesValue,
    isEquityLoading,
  };
}
